<template>
  <b-card class="wam-card" data-test="wa-messages-card">
    <h4 class="wam-title">WhatsApp messages</h4>
    <p class="wam-intro">
      These are the messages your office can send on WhatsApp. Teamio sends them automatically &mdash; for
      example, your appointment reminders. Switch a message off and patients get a text instead. You can
      change the wording of a message; WhatsApp reviews each change before it goes out.
    </p>
    <p class="wam-intro wam-intro--note" data-test="wa-messages-texts-note">
      Reminders that list family members&rsquo; names, and pre-medication reminders, are always sent as texts.
    </p>

    <!-- Add new message: a message the office writes itself, for staff to send by hand from a conversation. -->
    <div v-if="!loading && !error" class="wam-add" data-test="wa-message-add-bar">
      <button
        type="button"
        class="wam-btn wam-btn--primary"
        :disabled="addIsOff"
        data-test="wa-message-add"
        @click="startCreate"
      >Add new message</button>
      <span v-if="limitReached" class="wam-muted-inline" data-test="wa-message-add-limit">
        You&rsquo;ve reached the limit of {{ maxCustom }} messages of your own.
      </span>
      <span v-else class="wam-muted-inline" data-test="wa-message-add-hint">
        Write your own message for your staff to send from a conversation.
      </span>
    </div>

    <p v-if="createdNote" class="wam-created" role="status" data-test="wa-message-created">{{ createdNote }}</p>

    <div v-if="creating" class="wam-create" data-test="wa-message-create">
      <h5 class="wam-row__title">New message</h5>
      <WhatsAppMessageEditor
        create
        :initial-name="createPrefill.name"
        :initial-body="createPrefill.body"
        :message="createStub"
        :submitting="submitting"
        :error="createError"
        @submit="saveCreate($event)"
        @cancel="cancelCreate"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="wam-loading" data-test="wa-messages-loading">
      <b-spinner small class="mr-2" />
      Loading your WhatsApp messages&hellip;
    </div>

    <!-- Could not load: small and muted, with its own retry. Never breaks the rest of the tab. -->
    <p v-else-if="error" class="wam-muted" data-test="wa-messages-error">
      We couldn&rsquo;t load your WhatsApp messages right now.
      <button type="button" class="wam-retry" data-test="wa-messages-retry" @click="load()">Try again</button>
    </p>

    <p v-else-if="!templates.length" class="wam-muted" data-test="wa-messages-empty">
      No WhatsApp messages yet.
    </p>

    <!-- The messages table: one row per message. A grid, like the wiki pages list; on a phone each row is a card. -->
    <div v-else class="wam-table" data-test="wa-messages-table">
      <div class="wam-head wam-grid" data-test="wa-messages-header" aria-hidden="true">
        <div>Message</div>
        <div class="wam-col-snippet">What it says</div>
        <div>Status</div>
        <div>Send on WhatsApp</div>
        <div class="wam-col-actions">Actions</div>
      </div>

      <div
        v-for="(t, i) in templates"
        :key="t.key + '-' + i"
        class="wam-item"
        :class="{ 'wam-item--off': t.setup === 'live' && !t.enabled && !t.custom }"
        data-test="wa-message-row"
      >
        <div class="wam-grid wam-line">
          <div class="wam-cell wam-cell--message">
            <h5 class="wam-row__title" data-test="wa-message-title">{{ t.title }}</h5>
            <p v-if="t.usedFor" class="wam-used-for" data-test="wa-message-used-for">{{ t.usedFor }}</p>
          </div>

          <div class="wam-cell wam-col-snippet" data-test="wa-message-snippet">
            <span v-if="snippet(t)" class="wam-snippet">{{ snippet(t) }}</span>
            <span v-else class="wam-muted-inline">The wording isn&rsquo;t available right now.</span>
          </div>

          <div class="wam-cell wam-cell--status">
            <span
              class="wam-pill"
              :class="'wam-pill--' + statusClass(t.status)"
              data-test="wa-message-status"
            >{{ statusLabel(t) }}</span>
          </div>

          <div class="wam-cell wam-cell--switch" :class="{ 'wam-cell--empty': !actions(t).switch }">
            <template v-if="actions(t).switch">
              <button
                type="button"
                role="switch"
                class="wam-switch"
                :class="{ 'wam-switch--on': t.enabled }"
                :aria-checked="t.enabled ? 'true' : 'false'"
                :aria-label="'Send ' + t.title + ' on WhatsApp'"
                :disabled="switchIsOff(t)"
                data-test="wa-message-switch"
                @click="onSwitch(t)"
              ><span class="wam-switch__knob" /></button>
              <span class="wam-switch-hint" data-test="wa-message-switch-hint">{{ hint(t) }}</span>
            </template>
            <span v-else class="wam-muted-inline" data-test="wa-message-switch-none">&mdash;</span>
          </div>

          <div class="wam-cell wam-col-actions">
            <button
              v-if="actions(t).view && viewKey !== t.key && editingKey !== t.key"
              key="view"
              type="button"
              class="wam-btn"
              data-test="wa-message-view"
              @click="openView(t)"
            >View</button>
            <button
              v-else-if="actions(t).view && viewKey === t.key"
              key="hide"
              type="button"
              class="wam-btn"
              data-test="wa-message-hide"
              @click="closeView"
            >Hide</button>
            <button
              v-if="actions(t).edit && editingKey !== t.key"
              key="edit"
              type="button"
              class="wam-btn wam-btn--primary"
              :disabled="editIsOff(t)"
              data-test="wa-message-edit"
              @click="startEdit(t)"
            >Edit</button>
            <button
              v-if="actions(t).resend"
              type="button"
              class="wam-btn wam-btn--primary"
              :disabled="resendIsOff"
              data-test="wa-message-resend"
              @click="startResend(t)"
            >Edit and resend</button>
            <button
              v-if="actions(t).setUp"
              type="button"
              class="wam-btn wam-btn--primary"
              :disabled="busy"
              data-test="wa-message-setup"
              @click="askSetUp(t)"
            >Set up</button>
          </div>
        </div>

        <!-- Plain confirmation before a message starts going out on WhatsApp. -->
        <div
          v-if="confirm && confirm.key === t.key"
          class="wam-confirm"
          role="alertdialog"
          :aria-label="confirm.kind === 'setup' ? 'Set up this message' : 'Switch this message on'"
          data-test="wa-message-confirm"
        >
          <p class="wam-confirm__text">{{ confirmMessage }}</p>
          <p v-if="confirm.kind === 'setup'" class="wam-confirm__text wam-confirm__text--small">
            WhatsApp reviews it first, so nothing changes for patients until it is approved.
          </p>
          <div class="wam-confirm__buttons">
            <button type="button" class="wam-btn wam-btn--primary" :disabled="busy" data-test="wa-message-confirm-yes" @click="confirmYes(t)">
              {{ confirm.kind === 'setup' ? 'Set up message' : 'Switch on' }}
            </button>
            <button type="button" class="wam-btn" :disabled="busy" data-test="wa-message-confirm-cancel" @click="confirm = null">Cancel</button>
          </div>
        </div>

        <p v-if="actionErrors[t.key]" class="wam-action-error" role="alert" data-test="wa-message-action-error">{{ actionErrors[t.key] }}</p>

        <!-- A message of the office's own that WhatsApp did not approve: say so plainly and how to fix it. -->
        <div
          v-if="t.custom && t.setup === 'not_approved'"
          class="wam-edit-state wam-edit-state--not_approved"
          data-test="wa-message-custom-not-approved"
        >
          <p class="wam-edit-state__title">{{ customNotApprovedTitle }}</p>
          <p class="wam-edit-state__text">{{ customNotApprovedHelp }}</p>
        </div>

        <WhatsAppMessageEditor
          v-if="editingKey === t.key"
          :message="t"
          :submitting="submitting"
          :error="editError"
          @submit="saveEdit(t, $event)"
          @cancel="cancelEdit"
        />

        <!-- A change waiting for approval, or not approved: always visible, it is the thing to act on. -->
        <div
          v-if="t.pendingEdit && editingKey !== t.key"
          class="wam-edit-state"
          :class="'wam-edit-state--' + t.pendingEdit.status"
          data-test="wa-message-pending-edit"
        >
          <p class="wam-edit-state__title" data-test="wa-message-pending-edit-title">{{ pendingTitle(t.pendingEdit) }}</p>
          <p class="wam-edit-state__text">{{ pendingText(t.pendingEdit, t) }}</p>
          <div v-if="t.pendingEdit.wording" class="wam-edit-state__wording" data-test="wa-message-pending-edit-wording">
            <WhatsAppMessageText :text="t.pendingEdit.wording" />
          </div>
        </div>

        <!-- The full wording, opened with View. -->
        <div v-if="viewKey === t.key && editingKey !== t.key" class="wam-details" data-test="wa-message-details">
          <div class="wam-block" data-test="wa-message-wording">
            <div class="wam-block__label">{{ t.pendingEdit ? (sendsNow(t) ? 'Current message (still sending)' : 'Current message') : 'Message' }}</div>
            <div class="wam-block__body">
              <WhatsAppMessageText v-if="t.wording" :text="t.wording" />
              <span v-else class="wam-muted-inline">The wording isn&rsquo;t available right now.</span>
            </div>
          </div>

          <div v-if="t.example" class="wam-block" data-test="wa-message-example">
            <div class="wam-block__label">What patients see</div>
            <div class="wam-block__body">
              <WhatsAppMessageText :text="t.example" />
            </div>
          </div>
        </div>

        <p v-if="t.setup === 'live' && !t.wording && editingKey !== t.key" class="wam-muted" data-test="wa-message-contact">
          Contact Teamio if you&rsquo;d like to change this message.
        </p>

        <p
          v-for="(label, mi) in t.missing"
          :key="mi"
          class="wam-missing"
          data-test="wa-message-missing"
        >{{ warning(label) }}</p>
      </div>
    </div>
  </b-card>
</template>

<script>
/**
 * My Account -> WhatsApp -> "WhatsApp messages" (Phase 1 read-only 2026-10-08; Phase 2 editing 2026-10-10;
 * Phase 3 the messages TABLE 2026-10-11).
 *
 * One row per message the office can send on WhatsApp - the standard set (appointment reminder, confirmation,
 * same-day, recall, booked) whether or not it is set up yet. Each row shows what the message is for, a short
 * line of what it says, where it is with WhatsApp, a "Send on WhatsApp" switch, and what can be done with it:
 *   View   the full wording and what patients see
 *   Edit   change the wording (WhatsApp reviews it; the live message keeps sending meanwhile)
 *   Set up submit a standard message the office does not have yet (a plain confirmation first)
 * The switch moves one message between WhatsApp and a text; switching ON asks first.
 *
 * "Add new message" (Step 4, 2026-10-11): the office writes a message of its own (a name and the text, blanks limited to
 * [Practice name] and [Office phone], at most 10). It goes to WhatsApp for approval and lists here like the others, except
 * it has no switch and no Set up: staff only ever send it by hand from a conversation, never automatically.
 *
 * Laid out as a grid like the wiki pages list (WikiHome.scss): the snippet column drops on a tablet and each row
 * becomes a card on a phone.
 *
 * Rendered by WhatsAppTab.vue only while WhatsApp is live. The load, the save, the set-up and the switch all go
 * through the shared services/whatsappMessages.js - the same file the Appointment reminders note loads through.
 *
 * A failed load shows a small retry line INSIDE this card; it must never take down the rest of the tab, which is
 * why it keeps its own error state instead of reporting to the tab's alert. A failed SAVE stays in the open
 * editor so what was typed is not lost; a failed set-up or switch says so on its own row and changes nothing.
 */
import {
  fetchWhatsAppMessages, submitWhatsAppEdit, setupWhatsAppMessage, setWhatsAppMessageEnabled, addWhatsAppCustomMessage,
} from '@/services/whatsappMessages';
import {
  statusKey, rowStatusLabel, missingWarning, editErrorMessage, snippetOf, rowActions, switchHint,
  CONFIRM_MOVE_TO_WHATSAPP, SETUP_FALLBACK_MESSAGE, SWITCH_FALLBACK_MESSAGE, actionErrorMessage,
  MAX_CUSTOM_MESSAGES, customLimitReached, customErrorMessage, customTitlePreview,
  CUSTOM_NOT_APPROVED_LABEL, CUSTOM_NOT_APPROVED_HELP,
} from '@/utils/whatsappMessages';
import { hasSmsNumber, isSmsOutboundDisabled } from '@/utils/siteCapability';
import WhatsAppMessageText from './WhatsAppMessageText.vue';
import WhatsAppMessageEditor from './WhatsAppMessageEditor.vue';

export default {
  name: 'WhatsAppMessagesCard',

  components: { WhatsAppMessageText, WhatsAppMessageEditor },

  props: {
    siteID: { type: String, default: '' },
  },

  data() {
    return {
      loading: true,
      error: false,
      templates: [],
      editingKey: null, // the one message whose editor is open
      viewKey: null, // the one message whose full wording is open
      submitting: false,
      editError: '',
      busy: false, // a set-up or switch is in flight
      confirm: null, // { key, kind: 'setup' | 'on' }
      actionErrors: {}, // key -> sentence for the practice
      creating: false, // the "Add new message" form is open
      createPrefill: { name: '', body: '' }, // set when a refused message is reopened to fix and send again
      customNotApprovedTitle: CUSTOM_NOT_APPROVED_LABEL,
      customNotApprovedHelp: CUSTOM_NOT_APPROVED_HELP,
      createError: '',
      createdNote: '', // what to tell the practice after a new message was sent for approval
      maxCustom: MAX_CUSTOM_MESSAGES,
    };
  },

  computed: {
    limitReached() {
      return customLimitReached(this.templates);
    },

    /** One thing at a time: not while a save, set-up or switch is running, nor with the form already open. */
    addIsOff() {
      return this.limitReached || this.creating || this.submitting || this.busy;
    },

    /** "Edit and resend" is off while a form is open or anything is saving. */
    resendIsOff() {
      return this.creating || this.submitting || this.busy;
    },

    /** The new-message form has no row yet; it only needs this practice's own name and phone for the preview. */
    createStub() {
      const first = this.templates.find((t) => t && t.sampleValues);
      return { key: '', wording: null, pendingEdit: null, custom: true, sampleValues: first ? first.sampleValues : {} };
    },

    /**
     * A WhatsApp-only office sends nothing as a text, so a message switched off there would reach no one.
     * Read from the store's settings the way the Appointment reminders page does (utils/siteCapability).
     */
    textsOff() {
      const customer = this.$store && this.$store.state && this.$store.state.customer;
      const settings = customer && customer.allSettings;
      return Boolean(settings) && hasSmsNumber(settings) && isSmsOutboundDisabled(settings);
    },

    confirmMessage() {
      return CONFIRM_MOVE_TO_WHATSAPP;
    },
  },

  watch: {
    siteID() {
      this.editingKey = null;
      this.viewKey = null;
      this.confirm = null;
      this.actionErrors = {};
      this.editError = '';
      this.creating = false;
      this.createPrefill = { name: '', body: '' };
      this.createError = '';
      this.createdNote = '';
      this.load();
    },
  },

  created() {
    // Not reactive on purpose: a counter that only guards against a slow older response landing
    // after a newer one (Try again, or the site changing mid-flight).
    this.requestSeq = 0;
  },

  mounted() {
    this.load();
  },

  beforeDestroy() {
    this.requestSeq = -1; // a response arriving after unmount must not touch the component
  },

  methods: {
    statusLabel(row) {
      return rowStatusLabel(row);
    },

    /** The pill's colour class: the five known statuses, anything else the neutral grey. */
    statusClass(status) {
      return statusKey(status);
    },

    snippet(t) {
      return snippetOf(t);
    },

    actions(t) {
      return rowActions(t, { textsOff: this.textsOff });
    },

    hint(t) {
      return switchHint(t, { textsOff: this.textsOff });
    },

    warning(label) {
      return missingWarning(label);
    },

    /** The switch is off-limits while anything is being saved, and always on a WhatsApp-only office. */
    switchIsOff(t) {
      return this.busy || this.submitting || this.actions(t).switchLocked;
    },

    /** One edit in flight per message (the server enforces it too), and none while a save is running. */
    editIsOff(t) {
      return this.submitting || Boolean(t.pendingEdit && t.pendingEdit.status === 'pending');
    },

    pendingTitle(edit) {
      return edit.status === 'pending'
        ? 'Your change is waiting for WhatsApp’s approval'
        : 'WhatsApp didn’t approve your last change';
    },

    /**
     * Is this message going out to patients right now? Only a live standard message whose switch is ON. A message
     * switched off, and every message the office wrote itself (staff send those by hand), is NOT being sent, so
     * "keeps sending" / "still being sent" would be wrong for it.
     */
    sendsNow(t) {
      return !(t && t.custom) && !(t && t.enabled === false);
    },

    pendingText(edit, t) {
      const sending = this.sendsNow(t);
      if (edit.status === 'pending') {
        return sending ? 'Your current message keeps sending until then.' : 'Your current message stays as it is until then.';
      }
      return sending
        ? 'Your current message is still being sent. You can change the wording and send it again.'
        : 'Your current message is unchanged. You can change the wording and send it again.';
    },

    openView(t) {
      this.viewKey = t.key;
    },

    closeView() {
      this.viewKey = null;
    },

    startEdit(t) {
      if (this.submitting) return;
      this.editingKey = t.key;
      this.viewKey = null;
      this.confirm = null;
      this.editError = '';
      this.creating = false;
      this.createdNote = '';
    },

    // -- add new message -------------------------------------------------------------------------

    startCreate() {
      if (this.addIsOff) return;
      this.createPrefill = { name: '', body: '' };
      this.creating = true;
      this.createError = '';
      this.createdNote = '';
      this.editingKey = null;
      this.viewKey = null;
      this.confirm = null;
    },

    /**
     * A message of the office's own that WhatsApp did not approve: reopen the form with its name and text so they can
     * fix it and send it again. Sending the same name again is how the server makes the next version of the same message.
     */
    startResend(t) {
      if (this.resendIsOff) return;
      this.createPrefill = { name: t.title, body: t.wording || '' };
      this.creating = true;
      this.createError = '';
      this.createdNote = '';
      this.editingKey = null;
      this.viewKey = null;
      this.confirm = null;
    },

    cancelCreate() {
      if (this.submitting) return;
      this.creating = false;
      this.createError = '';
    },

    /**
     * Send a NEW message of the office's own for WhatsApp's approval. A refusal (the name, the wording, the limit of 10)
     * keeps the form open with what was typed and the server's own sentence; success closes it and says where the
     * message went.
     */
    async saveCreate({ name, body }) {
      if (this.submitting) return;
      this.submitting = true;
      this.createError = '';
      try {
        const result = await addWhatsAppCustomMessage(this.siteID, name, body, this.$store.state.getAuth());
        this.creating = false;
        // Named from the key the server gave it (a second "Late fee" becomes "Late fee 2"), as the list will show it.
        this.createdNote = `“${customTitlePreview(result.key.replace(/^custom-/, ''))}” was sent to WhatsApp for approval. You can see it in the list below. Once it’s approved, your staff can send it from a conversation.`;
        this.load({ quiet: true });
      } catch (e) {
        console.error('WhatsApp messages: new message failed:', e);
        const { code, message } = customErrorMessage(e);
        this.createError = message;
        // At the limit somewhere else: the list has moved on, so show it as it is now.
        if (code === 'custom_limit_reached') this.load({ quiet: true });
      } finally {
        this.submitting = false;
      }
    },

    cancelEdit() {
      if (this.submitting) return;
      this.editingKey = null;
      this.editError = '';
    },

    async saveEdit(t, typed) {
      if (this.submitting) return;
      this.submitting = true;
      this.editError = '';
      try {
        await submitWhatsAppEdit(this.siteID, t.key, typed, this.$store.state.getAuth());
        // Show the waiting state at once; the quiet reload below replaces it with what the server holds.
        this.templates = this.templates.map((x) => (x.key === t.key
          ? { ...x, pendingEdit: { status: 'pending', wording: typed.trim() } }
          : x));
        this.editingKey = null;
        this.load({ quiet: true });
      } catch (e) {
        console.error('WhatsApp messages: save failed:', e);
        const { code, message } = editErrorMessage(e);
        this.editError = message;
        // Someone else's change is already waiting: the row has moved on, so show it as it is now.
        if (code === 'edit_in_progress') {
          this.editingKey = null;
          this.load({ quiet: true });
        }
      } finally {
        this.submitting = false;
      }
    },

    // -- set up ---------------------------------------------------------------------------------

    askSetUp(t) {
      if (this.busy) return;
      this.confirm = { key: t.key, kind: 'setup' };
      this.$set(this.actionErrors, t.key, '');
    },

    // -- the switch -----------------------------------------------------------------------------

    onSwitch(t) {
      if (this.switchIsOff(t)) return;
      this.$set(this.actionErrors, t.key, '');
      // Switching OFF is always safe (patients just get a text again), so it needs no question. Switching ON
      // moves the message to WhatsApp, so it asks first.
      if (t.enabled) this.applySwitch(t, false);
      else this.confirm = { key: t.key, kind: 'on' };
    },

    confirmYes(t) {
      if (!this.confirm || this.confirm.key !== t.key) return;
      if (this.confirm.kind === 'setup') this.applySetUp(t);
      else this.applySwitch(t, true);
    },

    async applySetUp(t) {
      if (this.busy) return;
      this.busy = true;
      try {
        await setupWhatsAppMessage(this.siteID, t.key, this.$store.state.getAuth());
        this.confirm = null;
        // Show the waiting state at once; the quiet reload below replaces it with what the server holds.
        this.templates = this.templates.map((x) => (x.key === t.key ? { ...x, setup: 'waiting', status: 'pending' } : x));
        this.load({ quiet: true });
      } catch (e) {
        console.error('WhatsApp messages: set up failed:', e);
        this.confirm = null;
        const { code, message } = actionErrorMessage(e, SETUP_FALLBACK_MESSAGE);
        this.$set(this.actionErrors, t.key, message);
        // Already set up (or being set up) somewhere else: the row has moved on, so show it as it is now.
        if (code === 'message_already_set_up') this.load({ quiet: true });
      } finally {
        this.busy = false;
      }
    },

    async applySwitch(t, enabled) {
      if (this.busy) return;
      this.busy = true;
      const before = this.templates;
      // Flip it on screen at once; put it back if the server says no.
      this.templates = this.templates.map((x) => (x.key === t.key ? { ...x, enabled } : x));
      try {
        await setWhatsAppMessageEnabled(this.siteID, t.key, enabled, this.$store.state.getAuth());
        this.confirm = null;
        this.load({ quiet: true });
      } catch (e) {
        console.error('WhatsApp messages: switch failed:', e);
        this.templates = before;
        this.confirm = null;
        const { message } = actionErrorMessage(e, SWITCH_FALLBACK_MESSAGE);
        this.$set(this.actionErrors, t.key, message);
      } finally {
        this.busy = false;
      }
    },

    /**
     * @param {{ quiet?: boolean }} [opts] quiet = refresh in place (no spinner, a failure keeps what is
     *        on screen); the default is the first / retry load.
     */
    async load({ quiet = false } = {}) {
      const seq = ++this.requestSeq;
      if (!quiet) {
        this.loading = true;
        this.error = false;
      }
      try {
        const templates = await fetchWhatsAppMessages(this.siteID, this.$store.state.getAuth(), { all: true });
        if (seq !== this.requestSeq) return;
        this.templates = templates;
      } catch (e) {
        if (seq !== this.requestSeq) return;
        console.error('WhatsApp messages: load failed:', e);
        if (!quiet) {
          this.templates = [];
          this.error = true;
        }
      } finally {
        // Not gated on `quiet`: a quiet refresh that supersedes a spinner-showing load must not leave it up.
        if (seq === this.requestSeq) this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Same look as the tab's own cards (.wst-card in WhatsAppTab.vue), from the shared My Account
   tokens. The table is a grid like the wiki pages list (WikiHome.scss): header row, rows with a teal edge on
   hover, a column dropped on a tablet, a card per row on a phone. No fixed widths on message text and nothing
   on a message is nowrap, so it reads at 375px. */
@import './PatientPayments/tokens';

$wam-columns: minmax(180px, 1.1fr) minmax(0, 1.7fr) minmax(130px, 0.7fr) minmax(150px, 0.8fr) minmax(130px, auto);

.wam-card {
  margin-top: 1rem;
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
}

.wam-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $pp-text-strong;
  margin: 0 0 0.5rem;
}

.wam-intro {
  margin: 0 0 0.25rem;
  color: $pp-text-muted;
  font-size: 0.88rem;
  line-height: 1.6;
  max-width: 640px;
}

.wam-intro--note {
  font-size: 0.82rem;
}

.wam-loading {
  display: flex;
  align-items: center;
  color: $pp-text-muted;
  font-size: 0.9rem;
  padding: 0.75rem 0 0.25rem;
}

.wam-muted {
  margin: 0.75rem 0 0;
  color: $pp-text-muted;
  font-size: 0.85rem;
  line-height: 1.6;
}

.wam-muted-inline {
  color: $pp-text-muted;
}

/* ── Add new message ──────────────────────────────────────────────────────────────────────────── */
.wam-add {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.8rem;
  margin: 0.9rem 0 0.2rem;
  font-size: 0.85rem;
  line-height: 1.5;
}

.wam-create {
  margin: 0.8rem 0 0.4rem;
  padding: 0.8rem 1rem 1rem;
  border: 1px solid $pp-brand-border;
  border-radius: $pp-radius-sm;
  background: $pp-surface;
}

.wam-created {
  margin: 0.7rem 0 0;
  padding: 0.55rem 0.8rem;
  border: 1px solid $pp-brand-border;
  border-radius: $pp-radius-sm;
  background: $pp-brand-subtle;
  color: $pp-text-strong;
  font-size: 0.85rem;
  line-height: 1.5;
}

/* A link-looking button: the retry is an action, not navigation. */
.wam-retry {
  padding: 0;
  border: 0;
  background: none;
  color: $pp-brand;
  font-size: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;

  &:hover { color: $pp-brand-deep; }
}

/* ── the table ───────────────────────────────────────────────────────────────────────────────── */
.wam-table {
  margin-top: 1rem;
  border: 1px solid $pp-border;
  border-radius: $pp-radius-sm;
  background: $pp-surface;
  overflow: hidden;
}

.wam-grid {
  display: grid;
  grid-template-columns: $wam-columns;
  column-gap: 1rem;
  align-items: center;
}

.wam-head {
  padding: 0.65rem 1rem;
  background: $pp-surface-sunken;
  border-bottom: 1px solid $pp-border;
  color: $pp-text-muted;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.wam-item {
  padding: 0.85rem 1rem;
  border-bottom: 1px solid $pp-border;
  border-left: 3px solid transparent;
  background: $pp-surface;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:last-child { border-bottom: 0; }
  &:hover { background: $pp-surface-sunken; border-left-color: $pp-brand; }
}

.wam-cell {
  min-width: 0;
}

.wam-row__title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: $pp-text-strong;
  overflow-wrap: anywhere;
}

.wam-used-for {
  margin: 0.15rem 0 0;
  color: $pp-text-muted;
  font-size: 0.8rem;
  line-height: 1.4;
}

.wam-snippet {
  color: $pp-text-strong;
  font-size: 0.85rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.wam-cell--switch {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
}

.wam-switch-hint {
  color: $pp-text-muted;
  font-size: 0.75rem;
  line-height: 1.3;
}

.wam-col-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem;
}

.wam-pill {
  display: inline-block;
  padding: 0.15rem 0.65rem;
  border: 1px solid $pp-border;
  border-radius: $pp-radius-pill;
  background: $pp-surface-sunken;
  color: $pp-text-muted;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.4;
}

.wam-pill--approved {
  background: $pp-brand-subtle;
  border-color: $pp-brand-border;
  color: $pp-brand-deep;
}

.wam-pill--pending {
  background: $pp-amber-subtle;
  border-color: $pp-amber-border;
  color: $pp-amber-text;
}

.wam-pill--not_approved {
  background: $pp-red-subtle;
  border-color: $pp-red-border;
  color: $pp-red-text;
}

/* ── the switch ──────────────────────────────────────────────────────────────────────────────── */
.wam-switch {
  position: relative;
  flex: none;
  min-width: 2.5rem;
  height: 1.4rem;
  padding: 0;
  border: 1px solid $pp-border-strong;
  border-radius: 999px;
  background: $pp-border-strong;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &--on { background: $pp-brand; border-color: $pp-brand; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid $pp-brand-deep; outline-offset: 2px; }
}

.wam-switch__knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(1.4rem - 6px);
  height: calc(1.4rem - 6px);
  border-radius: 50%;
  background: #fff;
  transition: transform 0.15s ease;
}

.wam-switch--on .wam-switch__knob {
  transform: translateX(1.1rem);
}

/* ── buttons ─────────────────────────────────────────────────────────────────────────────────── */
.wam-btn {
  padding: 0.15rem 0.85rem;
  border: 1px solid $pp-border-strong;
  border-radius: $pp-radius-pill;
  background: $pp-surface;
  color: $pp-text-strong;
  font-size: 0.83rem;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;

  &:hover:not(:disabled) { background: $pp-surface-sunken; }
  &:disabled {
    border-color: $pp-border;
    color: $pp-text-subtle;
    cursor: not-allowed;
  }
}

.wam-btn--primary {
  border-color: $pp-brand;
  color: $pp-brand-deep;

  &:hover:not(:disabled) { background: $pp-brand-subtle; }
}

/* ── under a row ─────────────────────────────────────────────────────────────────────────────── */
.wam-confirm {
  margin-top: 0.7rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid $pp-brand-border;
  border-radius: $pp-radius-sm;
  background: $pp-brand-subtle;
  color: $pp-text-strong;
  font-size: 0.85rem;
  line-height: 1.5;
}

.wam-confirm__text {
  margin: 0;
}

.wam-confirm__text--small {
  margin-top: 0.15rem;
  color: $pp-text-muted;
  font-size: 0.8rem;
}

.wam-confirm__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.6rem;
}

.wam-action-error {
  margin: 0.7rem 0 0;
  padding: 0.55rem 0.8rem;
  border: 1px solid $pp-red-border;
  border-radius: $pp-radius-sm;
  background: $pp-red-subtle;
  color: $pp-red-text;
  font-size: 0.83rem;
  line-height: 1.5;
}

.wam-details {
  margin-top: 0.4rem;
}

.wam-block {
  margin-top: 0.8rem;
}

.wam-block__label {
  margin-bottom: 0.25rem;
  color: $pp-text-muted;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.wam-block__body {
  padding: 0.6rem 0.8rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  color: $pp-text-strong;
  font-size: 0.88rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.wam-missing {
  margin: 0.8rem 0 0;
  padding: 0.55rem 0.8rem;
  border: 1px solid $pp-amber-border;
  border-radius: $pp-radius-sm;
  background: $pp-amber-subtle;
  color: $pp-amber-text;
  font-size: 0.83rem;
  line-height: 1.5;
}

.wam-edit-state {
  margin-top: 0.8rem;
  padding: 0.65rem 0.8rem;
  border: 1px solid;
  border-radius: $pp-radius-sm;
  font-size: 0.83rem;
  line-height: 1.5;
}

.wam-edit-state--pending {
  border-color: $pp-amber-border;
  background: $pp-amber-subtle;
  color: $pp-amber-text;
}

.wam-edit-state--not_approved {
  border-color: $pp-red-border;
  background: $pp-red-subtle;
  color: $pp-red-text;
}

.wam-edit-state__title {
  margin: 0;
  font-weight: 700;
}

.wam-edit-state__text {
  margin: 0.15rem 0 0;
}

.wam-edit-state__wording {
  margin-top: 0.5rem;
  padding: 0.5rem 0.7rem;
  border-radius: $pp-radius-sm;
  background: rgba(255, 255, 255, 0.7);
  color: $pp-text-strong;
  overflow-wrap: anywhere;
}

/* ── a tablet: the snippet column goes, as the wiki list drops its tags column ──────────────── */
@media (max-width: 991px) {
  .wam-grid {
    grid-template-columns: minmax(160px, 1.2fr) minmax(120px, 0.7fr) minmax(140px, 0.8fr) minmax(120px, auto);
  }

  .wam-head .wam-col-snippet,
  .wam-line .wam-col-snippet {
    display: none;
  }
}

/* ── a phone: no header, each row is a card, the actions sit under a rule ───────────────────── */
@media (max-width: 768px) {
  .wam-head {
    display: none;
  }

  .wam-table {
    border: 0;
    border-radius: 0;
    overflow: visible;
  }

  .wam-item {
    margin-top: 0.75rem;
    padding: 0.85rem 0.9rem;
    border: 1px solid $pp-border;
    border-left-width: 3px;
    border-radius: $pp-radius-sm;

    &:last-child { border-bottom: 1px solid $pp-border; }
  }

  .wam-line.wam-grid {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.55rem;
  }

  .wam-line .wam-col-snippet {
    display: block;
  }

  .wam-cell--switch {
    flex-direction: row;
    align-items: center;
    gap: 0.6rem;
  }

  /* A message with no switch (not set up yet): a lone dash on a card is just noise. */
  .wam-cell--empty {
    display: none;
  }

  .wam-col-actions {
    justify-content: flex-start;
    padding-top: 0.6rem;
    border-top: 1px solid $pp-border;
  }

  .wam-btn {
    min-height: 2.25rem;
  }
}
</style>
